import dayjs from "dayjs";

export default {
  adaptive:
    ({ mindset }) =>
    (a, b) => {
      if (!a.attributes || !b.attributes) {
        return 0;
      }

      const {
        difficulty: aDifficulty,
        importance: aImportance,
        fun: aFun,
      } = a.attributes;
      const {
        difficulty: bDifficulty,
        importance: bImportance,
        fun: bFun,
      } = b.attributes;

      // Wellbeing factor: 0 (rock bottom) to 1 (feeling great)
      // Averages mood and energy, then normalizes to 0-1 range
      const wellbeing = ((mindset.mood + mindset.energy) / 2 - 1) / 9;

      // Fun/difficulty are scaled up when feeling bad, scaled down when feeling great
      const funDifficultyMultiplier = 2 + (1 - wellbeing) * 2; // Range: 2 (great) to 4 (rock bottom)

      const getFunWeight = (fun, mood) =>
        funDifficultyMultiplier *
        (9 - Math.abs(Math.abs(9 - (mood - 1)) - (fun - 1)));

      const getDifficultyWeight = (difficulty, energy) =>
        funDifficultyMultiplier *
        Math.abs(Math.abs(9 - (difficulty - 1)) - (energy - 1));

      // Importance weight: scales with wellbeing
      // Rock bottom: importance has minimal influence
      // Middling: importance has moderate influence
      // Feeling great: importance dominates
      const getImportanceWeight = (importance) => {
        // importanceMultiplier ranges from 0.3 (rock bottom) to 2.5 (feeling great)
        const importanceMultiplier = 0.3 + wellbeing * 2.2;
        const base = importance * importanceMultiplier;

        // Critical task boost: importance 9-10 always gets a significant floor
        // so truly critical tasks never sink to the bottom, even at rock bottom
        if (importance >= 10) {
          return base + 20 + wellbeing * 15;
        } else if (importance >= 9) {
          return base + 15 + wellbeing * 10;
        } else if (importance >= 8) {
          return base + 2 + wellbeing * 10;
        } else if (importance >= 7) {
          return base + 1 + wellbeing * 4;
        }
        return base;
      };

      // Urgency weight: graduated boost based on deadline proximity
      // Ensures tasks with approaching deadlines stay visible regardless of mood
      const getUrgencyWeight = (task) => {
        if (!task.deadline) return 0;

        const createdInSeconds = Math.round(task.created / 1000);
        const timeTillDeadline = task.deadline - dayjs().unix();
        const timeToComplete = task.deadline - createdInSeconds;
        const timeToCompletePercent = Math.round(
          (timeTillDeadline / timeToComplete) * 100,
        );

        // Past deadline or overdue
        if (timeToCompletePercent <= 0) {
          return 20; // Overdue: significant boost
        } else if (timeToCompletePercent <= 10) {
          return 15; // Critical: <10% time remaining
        } else if (timeToCompletePercent <= 25) {
          return 10; // Urgent: <25% time remaining
        } else if (timeToCompletePercent <= 50) {
          return 6; // Approaching: <50% time remaining
        } else if (timeToCompletePercent <= 75) {
          return 3; // Some pressure: <75% time remaining
        }
        return 1; // Has deadline but plenty of time
      };

      const aUrgency = getUrgencyWeight(a);
      const bUrgency = getUrgencyWeight(b);

      const aWeight =
        getDifficultyWeight(aDifficulty, mindset.energy) +
        getFunWeight(aFun, mindset.mood) +
        aUrgency +
        getImportanceWeight(aImportance) +
        (a.targeted ? 10 : 0);
      const bWeight =
        getDifficultyWeight(bDifficulty, mindset.energy) +
        getFunWeight(bFun, mindset.mood) +
        bUrgency +
        getImportanceWeight(bImportance) +
        (b.targeted ? 10 : 0);

      if (aWeight > bWeight) {
        return -1;
      }
      if (aWeight < bWeight) {
        return 1;
      }

      return 0;
    },
  "importance-asc": () => (a, b) =>
    a.attributes.importance - b.attributes.importance,
  "importance-desc": () => (a, b) =>
    b.attributes.importance - a.attributes.importance,
  "difficulty-asc": () => (a, b) =>
    a.attributes.difficulty - b.attributes.difficulty,
  "difficulty-desc": () => (a, b) =>
    b.attributes.difficulty - a.attributes.difficulty,
  "fun-asc": () => (a, b) => a.attributes.fun - b.attributes.fun,
  "fun-desc": () => (a, b) => b.attributes.fun - a.attributes.fun,
  "name-asc": () => (a, b) => a.name.localeCompare(b.name),
  "name-desc": () => (a, b) => b.name.localeCompare(a.name),
  "created-asc": () => (a, b) => dayjs(a.created) - dayjs(b.created),
  "created-desc": () => (a, b) => dayjs(b.created) - dayjs(a.created),
  "deadline-asc": () => (a, b) => {
    if (a.deadline && b.deadline) {
      return dayjs(a.deadline) - dayjs(b.deadline);
    } else if (a.deadline) {
      return -1; // Prioritize tasks with deadlines
    } else if (b.deadline) {
      return 1; // Deprioritize tasks without deadlines
    } else {
      return 0; // Both tasks lack deadlines
    }
  },
  "deadline-desc": () => (a, b) => {
    if (a.deadline && b.deadline) {
      return dayjs(b.deadline) - dayjs(a.deadline);
    } else if (a.deadline) {
      return -1; // Prioritize tasks with deadlines
    } else if (b.deadline) {
      return 1; // Deprioritize tasks without deadlines
    } else {
      return 0; // Both tasks lack deadlines
    }
  },
  "schedule-asc": () => (a, b) => {
    if (a.schedule && b.schedule) {
      return dayjs(a.schedule) - dayjs(b.schedule);
    } else if (a.schedule) {
      return -1; // Prioritize tasks with schedules
    } else if (b.schedule) {
      return 1; // Deprioritize tasks without schedules
    } else {
      return 0; // Both tasks lack schedules
    }
  },
  "schedule-desc": () => (a, b) => {
    if (a.schedule && b.schedule) {
      return dayjs(b.schedule) - dayjs(a.schedule);
    } else if (a.schedule) {
      return -1; // Prioritize tasks with schedules
    } else if (b.schedule) {
      return 1; // Deprioritize tasks without schedules
    } else {
      return 0; // Both tasks lack schedules
    }
  },
};
